// =============================================================================
// src/core/map-controller.ts — Controlador do mapa Leaflet (porte de unified-app.js).
//
// Proveniencia: assets/unified-app.js `var MapController = {...}` (linhas 313-677).
//   Portado FIEL: centro [-3.10,-60.01]/zoom 12, basemap OpenStreetMap claro na aba
//   Mapa + CartoDB dark legado no tema escuro (Cinema), choropleth
//   de bairros via public/assets/geo-manaus-bairros.json + municipios via
//   geo-amazonas-municipios.json, camadas partido/tematica (estilos injetados por
//   setZoneStyles/setMunStyles), ghost layer, split mode, popups e drill-in. Cores,
//   opacidades, thresholds (ZOOM_THRESHOLD 10) e timings sao paridade — NAO alterar.
//
// Leaflet: `import L from 'leaflet'` + @types/leaflet 1.9.x. API (L.map/zoomSnap,
//   L.tileLayer/subdomains, L.geoJSON/setStyle/resetStyle/bringToFront, L.divIcon,
//   L.layerGroup, flyTo/flyToBounds, latLngToContainerPoint, latLngBounds) verificada
//   via Context7 (/leaflet/leaflet) em 2026-05-30 — superficie estavel no 1.9.
//
// Default de estilo: quando nenhuma styleFn esta setada (primeiro paint, antes de
//   Sections.mapaEleitoral.activate), o legado lia Sections.mapaEleitoral.currentYear
//   (default '2024'). Aqui usamos MAP_DEFAULT_YEAR='2024' diretamente do dataset —
//   mesmo resultado visual, sem acoplar o controller ao estado da view.
// =============================================================================

import L from 'leaflet';
import type {
  Map as LeafletMap,
  GeoJSON as LeafletGeoJSON,
  LayerGroup as LeafletLayerGroup,
  Layer,
  Marker,
  LatLng,
  LatLngBounds,
  LatLngExpression,
  PathOptions,
  LeafletMouseEvent,
  GeoJSONOptions,
} from 'leaflet';
import type {
  MapController as MapControllerContract,
  MapTheme,
  ZonaKey,
  MunicipalityId,
  SectionId,
  MapMarkerDef,
  ZoneStyleFn,
  MunStyleFn,
  CinemaYearFrame,
  BairroFeatureProperties,
  MunicipioFeatureProperties,
} from '../app/types';
import {
  MANAUS_ZONA_KEY_MAP,
  MANAUS_ZONA_DISPLAY_NAME,
  MANAUS_ZONA_POP_DATA,
  MANAUS_ELECTORAL_BY_YEAR,
  getMunData,
} from '../data';
import { byId, qs } from '../app/dom';
import { buildMarkerDivIcon } from './marker-factory';
import { OSM_TILE_URL, OSM_ATTRIBUTION, OSM_MAX_ZOOM } from './osm-tiles';
import {
  LEGACY_CARTO_DARK_TILE_URL,
  LEGACY_CARTO_MAX_ZOOM,
  LEGACY_CARTO_SUBDOMAINS,
} from './carto-tiles';

/** Ano default do choropleth no primeiro paint (Sections.mapaEleitoral.currentYear). */
const MAP_DEFAULT_YEAR = '2024' as const;

/** Id IBGE da capital Manaus — recebe tratamento especial (tooltip/flyTo/maxZoom). */
const MANAUS_CAPITAL_ID: MunicipalityId = '1302603';

/** Caminhos dos GeoJSON servidos por public/assets (carregados via fetch). */
const BAIRROS_GEOJSON_URL = 'assets/geo-manaus-bairros.json';
// Malha LEVE do IBGE (intermediaria): este controller roda no boot de toda sessao
// (aba Mapa, mesmo desativada) e so usa os municipios como contexto da cidade — a
// malha maxima (~590 KB gzip) fica restrita ao Explorar (2026-10-13).
const MUNICIPIOS_GEOJSON_URL = 'assets/geo-amazonas-municipios-lite.json';

/** Feature de bairro (properties.ZONAS) usada para resolver a ZonaKey. */
type BairroFeature = GeoJSON.Feature<GeoJSON.Geometry, BairroFeatureProperties>;
/** Feature de municipio (properties.id/name). */
type MunicipioFeature = GeoJSON.Feature<GeoJSON.Geometry, MunicipioFeatureProperties>;

/** True quando o usuario pediu menos animacao (prefers-reduced-motion). */
function prefersReducedMotion(): boolean {
  return window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)').matches : false;
}

/**
 * Controlador do mapa Leaflet. Implementa o contrato MapControllerContract.
 * Exportado como singleton `MapController` (linha final) — mesma forma de uso do
 * runtime legado, sem global escondido: a integracao injeta a instancia no AppContext.
 */
class LeafletMapController implements MapControllerContract {
  map: LeafletMap | null = null;
  bairrosLayer: LeafletGeoJSON | null = null;
  municipiosLayer: LeafletGeoJSON | null = null;
  currentTheme: MapTheme = 'light';

  private ghostLayer: LeafletGeoJSON | null = null;
  private ghostLayerData: unknown = null;
  private lightTiles: L.TileLayer | null = null;
  private darkTiles: L.TileLayer | null = null;
  private zoneLabelMarkers: Marker[] = [];
  private zoneCentroids: Partial<Record<ZonaKey, LatLng>> = {};
  private markerLayers: Partial<Record<SectionId, LeafletLayerGroup>> = {};
  private selectedBairroLayer: Layer | null = null;
  private selectedMunLayer: Layer | null = null;
  private splitLayerLeft: LeafletGeoJSON | null = null;
  private splitLayerRight: LeafletGeoJSON | null = null;
  private currentZoneStyleFn: ZoneStyleFn | null = null;
  private currentMunStyleFn: MunStyleFn | null = null;

  /** Limiar de zoom: < 10 mostra municipios (estadual), >= 10 mostra bairros (Manaus). */
  private readonly ZOOM_THRESHOLD = 10;

  /** Cria o mapa, adiciona os tiles claros e busca os dois GeoJSON em paralelo. */
  init(): void {
    this.map = L.map('unifiedMap', {
      center: [-3.1, -60.01], zoom: 12, minZoom: 5, maxZoom: 16,
      zoomControl: true, attributionControl: true,
      zoomSnap: 0.5, zoomDelta: 0.5, wheelPxPerZoomLevel: 120,
    });
    this.lightTiles = L.tileLayer(OSM_TILE_URL, { maxZoom: OSM_MAX_ZOOM, attribution: OSM_ATTRIBUTION });
    this.darkTiles = L.tileLayer(LEGACY_CARTO_DARK_TILE_URL, {
      maxZoom: LEGACY_CARTO_MAX_ZOOM,
      subdomains: LEGACY_CARTO_SUBDOMAINS,
    });
    this.lightTiles.addTo(this.map);
    void this.fetchGeoData();
  }

  /** Busca bairros + municipios e monta as camadas quando ambos resolverem. */
  private async fetchGeoData(): Promise<void> {
    const [bairros, municipios] = await Promise.all([
      fetch(BAIRROS_GEOJSON_URL).then((r) => r.json()),
      fetch(MUNICIPIOS_GEOJSON_URL).then((r) => r.json()),
    ]);
    this.initLayers(bairros, municipios);
  }

  /** Resolve a ZonaKey de uma feature de bairro pelo rotulo bruto ZONAS (fallback 'norte'). */
  private getZonaKey(feature: BairroFeature): ZonaKey {
    return MANAUS_ZONA_KEY_MAP[feature.properties.ZONAS] ?? 'norte';
  }

  /** Estilo de uma zona: styleFn injetada ou o choropleth partidario default ('2024'). */
  private getZoneStyle(zonaKey: ZonaKey): PathOptions {
    if (this.currentZoneStyleFn) return this.currentZoneStyleFn(zonaKey);
    const fillColor = MANAUS_ELECTORAL_BY_YEAR[MAP_DEFAULT_YEAR][zonaKey][1];
    return { fillColor, fillOpacity: 0.72, weight: 0.8, color: 'rgba(255,255,255,0.6)', opacity: 1 };
  }

  /** Estilo de um municipio: styleFn injetada ou o partido vencedor default ('2024'). */
  private getMunStyle(munId: MunicipalityId): PathOptions {
    if (this.currentMunStyleFn) return this.currentMunStyleFn(munId);
    const fillColor = getMunData(munId, MAP_DEFAULT_YEAR)[1];
    return { fillColor, fillOpacity: 0.55, weight: 1, color: '#ffffff', opacity: 0.7 };
  }

  /** Monta camadas de bairros/municipios, centroides, zoom-handlers e fitBounds inicial. */
  private initLayers(bairrosData: unknown, municipiosData: unknown): void {
    qs('#mapLoading')?.remove();
    this.ghostLayerData = bairrosData;
    this.bairrosLayer = L.geoJSON(bairrosData as GeoJSON.GeoJsonObject, this.bairrosLayerOptions());
    this.buildZoneCentroids();
    this.municipiosLayer = L.geoJSON(municipiosData as GeoJSON.GeoJsonObject, this.municipiosLayerOptions());
    this.addInitialLayers();
    this.bindMapEvents();
    this.updateViewForZoom();
  }

  /** Opcoes da camada de bairros: estilo por zona + handlers de hover/click. */
  private bairrosLayerOptions(): GeoJSONOptions {
    return {
      style: (feature) => this.getZoneStyle(this.getZonaKey(feature as BairroFeature)),
      onEachFeature: (feature, layer) => this.bindBairroFeature(feature as BairroFeature, layer),
    };
  }

  /** Hover/click de um bairro: realce, selecao exclusiva e popup de zona. */
  private bindBairroFeature(feature: BairroFeature, layer: Layer): void {
    const zonaKey = this.getZonaKey(feature);
    layer.on('mouseover', (e: LeafletMouseEvent) => this.onBairroHover(e));
    layer.on('mouseout', (e: LeafletMouseEvent) => this.onBairroOut(e));
    layer.on('click', (e: LeafletMouseEvent) => this.onBairroClick(e, zonaKey));
  }

  /** Realca o bairro sob o cursor (exceto o ja selecionado) e o traz pra frente. */
  private onBairroHover(e: LeafletMouseEvent): void {
    if (e.target !== this.selectedBairroLayer) {
      e.target.setStyle({ weight: 2, color: '#1F1F1F', fillOpacity: 0.85 });
    }
    e.target.bringToFront();
  }

  /** Restaura o estilo do bairro ao sair (mantem o selecionado realcado). */
  private onBairroOut(e: LeafletMouseEvent): void {
    if (e.target !== this.selectedBairroLayer) this.bairrosLayer?.resetStyle(e.target);
  }

  /** Seleciona o bairro clicado (exclusivo), realca e abre o popup da zona. */
  private onBairroClick(e: LeafletMouseEvent, zonaKey: ZonaKey): void {
    L.DomEvent.stopPropagation(e);
    if (this.selectedBairroLayer) this.bairrosLayer?.resetStyle(this.selectedBairroLayer);
    this.selectedBairroLayer = e.target;
    e.target.setStyle({ weight: 2.5, color: '#1F1F1F', fillOpacity: 0.9 });
    e.target.bringToFront();
    this.showZonePopup(zonaKey, e.latlng);
  }

  /** Calcula centroides de zona (a partir dos bounds dos bairros) e cria os rotulos. */
  private buildZoneCentroids(): void {
    const zoneBounds = new Map<ZonaKey, LatLngBounds>();
    this.bairrosLayer?.eachLayer((layer) => {
      const key = this.getZonaKey((layer as L.GeoJSON & { feature: BairroFeature }).feature);
      const bounds = zoneBounds.get(key) ?? L.latLngBounds([]);
      bounds.extend((layer as L.GeoJSON).getBounds());
      zoneBounds.set(key, bounds);
    });
    zoneBounds.forEach((bounds, key) => this.addZoneLabel(key, bounds.getCenter()));
  }

  /** Cria o marcador-rotulo (divIcon) de uma zona em seu centro e o guarda. */
  private addZoneLabel(key: ZonaKey, center: LatLng): void {
    this.zoneCentroids[key] = center;
    const marker = L.marker(center, {
      icon: L.divIcon({ className: 'zone-label-icon', html: `<span>${MANAUS_ZONA_DISPLAY_NAME[key]}</span>`, iconSize: [0, 0] }),
      interactive: false,
      pane: 'tooltipPane',
    });
    this.zoneLabelMarkers.push(marker);
  }

  /** Opcoes da camada de municipios: estilo por id + tooltip + hover/click. */
  private municipiosLayerOptions(): GeoJSONOptions {
    return {
      style: (feature) => this.getMunStyle((feature as MunicipioFeature).properties.id),
      onEachFeature: (feature, layer) => this.bindMunFeature(feature as MunicipioFeature, layer),
    };
  }

  /** Tooltip + hover/click de um municipio (Manaus voa pro nivel de bairros ao clicar). */
  private bindMunFeature(feature: MunicipioFeature, layer: Layer): void {
    const munId = feature.properties.id;
    const isManaus = munId === MANAUS_CAPITAL_ID;
    layer.bindTooltip(feature.properties.name, {
      permanent: false, direction: 'center',
      className: isManaus ? 'municipio-tooltip-label capital' : 'municipio-tooltip-label',
    });
    layer.on('mouseover', (e: LeafletMouseEvent) => this.onMunHover(e, layer));
    layer.on('mouseout', (e: LeafletMouseEvent) => this.onMunOut(e, layer));
    layer.on('click', (e: LeafletMouseEvent) => this.onMunClick(e, isManaus));
  }

  /** Realca o municipio sob o cursor e abre seu tooltip. */
  private onMunHover(e: LeafletMouseEvent, layer: Layer): void {
    if (e.target !== this.selectedMunLayer) {
      e.target.setStyle({ fillOpacity: 0.75, weight: 2, color: '#1F1F1F' });
    }
    e.target.bringToFront();
    (layer as L.GeoJSON).openTooltip();
  }

  /** Restaura o estilo do municipio e fecha seu tooltip ao sair. */
  private onMunOut(e: LeafletMouseEvent, layer: Layer): void {
    if (e.target !== this.selectedMunLayer) this.municipiosLayer?.resetStyle(e.target);
    (layer as L.GeoJSON).closeTooltip();
  }

  /** Clique em municipio: Manaus voa pro nivel de bairros; demais nao reagem. */
  private onMunClick(e: LeafletMouseEvent, isManaus: boolean): void {
    L.DomEvent.stopPropagation(e);
    if (isManaus) this.map?.flyTo([-3.08, -59.97], 12, { duration: 1.5 });
  }

  /** Adiciona a camada inicial (bairros + rotulos) e enquadra Manaus. */
  private addInitialLayers(): void {
    if (!this.map || !this.bairrosLayer) return;
    this.bairrosLayer.addTo(this.map);
    this.zoneLabelMarkers.forEach((m) => m.addTo(this.map as LeafletMap));
    this.map.fitBounds(this.bairrosLayer.getBounds(), { padding: [30, 30] });
  }

  /** Liga zoomend (troca camada por nivel) e click no mapa (fecha popup/selecao). */
  private bindMapEvents(): void {
    this.map?.on('zoomend', () => this.updateViewForZoom());
    this.map?.on('click', () => this.onMapClick());
  }

  /** Click no fundo do mapa: esconde o popup e limpa a selecao de bairro. */
  private onMapClick(): void {
    qs('#u-popup')?.classList.remove('show');
    if (this.selectedBairroLayer && this.bairrosLayer) {
      this.bairrosLayer.resetStyle(this.selectedBairroLayer);
      this.selectedBairroLayer = null;
    }
  }

  setZoneStyles(styleFn: ZoneStyleFn): void {
    this.currentZoneStyleFn = styleFn;
    if (this.bairrosLayer && this.map?.hasLayer(this.bairrosLayer)) {
      this.bairrosLayer.setStyle((f) => this.getZoneStyle(this.getZonaKey(f as BairroFeature)));
    }
  }

  setMunStyles(styleFn: MunStyleFn): void {
    this.currentMunStyleFn = styleFn;
    if (this.municipiosLayer && this.map?.hasLayer(this.municipiosLayer)) {
      this.municipiosLayer.setStyle((f) => this.getMunStyle((f as MunicipioFeature).properties.id));
    }
  }

  /** Troca bairros<->municipios conforme o zoom cruza ZOOM_THRESHOLD (10). */
  private updateViewForZoom(): void {
    if (!this.map) return;
    if (this.map.getZoom() < this.ZOOM_THRESHOLD) this.showMunicipiosLevel();
    else this.showBairrosLevel();
  }

  /** Nivel estadual: remove bairros/rotulos, mostra municipios. */
  private showMunicipiosLevel(): void {
    const map = this.map as LeafletMap;
    if (this.bairrosLayer && map.hasLayer(this.bairrosLayer)) map.removeLayer(this.bairrosLayer);
    this.zoneLabelMarkers.forEach((m) => { if (map.hasLayer(m)) map.removeLayer(m); });
    if (this.municipiosLayer && !map.hasLayer(this.municipiosLayer)) this.municipiosLayer.addTo(map);
  }

  /** Nivel Manaus: remove municipios, mostra bairros/rotulos. */
  private showBairrosLevel(): void {
    const map = this.map as LeafletMap;
    if (this.municipiosLayer && map.hasLayer(this.municipiosLayer)) map.removeLayer(this.municipiosLayer);
    if (this.bairrosLayer && !map.hasLayer(this.bairrosLayer)) this.bairrosLayer.addTo(map);
    this.zoneLabelMarkers.forEach((m) => { if (!map.hasLayer(m)) m.addTo(map); });
  }

  setTheme(theme: MapTheme): void {
    if (theme === this.currentTheme || !this.map || !this.lightTiles || !this.darkTiles) return;
    this.currentTheme = theme;
    const [show, hide] = theme === 'dark' ? [this.darkTiles, this.lightTiles] : [this.lightTiles, this.darkTiles];
    if (this.map.hasLayer(hide)) this.map.removeLayer(hide);
    show.addTo(this.map);
  }

  /** Camada-fantasma de comparacao: pinta as zonas com as cores de outro ano (translucido). */
  showGhostLayer(yearData: CinemaYearFrame): void {
    if (!this.ghostLayerData || !this.map) return;
    if (this.ghostLayer) this.map.removeLayer(this.ghostLayer);
    this.ghostLayer = L.geoJSON(this.ghostLayerData as GeoJSON.GeoJsonObject, {
      style: (f) => ({
        fillColor: yearData.zones[this.getZonaKey(f as BairroFeature)],
        fillOpacity: 0.25, weight: 1.2, color: 'rgba(255,255,255,0.5)', opacity: 0.6, dashArray: '4,3',
      }),
      interactive: false,
    });
    this.ghostLayer.addTo(this.map);
    this.ghostLayer.bringToBack();
  }

  hideGhostLayer(): void {
    if (this.ghostLayer && this.map?.hasLayer(this.ghostLayer)) this.map.removeLayer(this.ghostLayer);
  }

  /** Injeta um conjunto de marcadores nomeado por secao (substitui o anterior de mesmo id). */
  addMarkerLayer(sectionId: SectionId, markerDefs: readonly MapMarkerDef[]): void {
    if (!this.map) return;
    this.removeMarkerLayer(sectionId);
    const group = L.layerGroup();
    markerDefs.forEach((def) => L.marker([def.lat, def.lng], { icon: buildMarkerDivIcon(def) }).addTo(group));
    group.addTo(this.map);
    this.markerLayers[sectionId] = group;
  }

  removeMarkerLayer(sectionId: SectionId): void {
    const group = this.markerLayers[sectionId];
    if (group && this.map) {
      this.map.removeLayer(group);
      delete this.markerLayers[sectionId];
    }
  }

  removeAllMarkers(): void {
    (Object.keys(this.markerLayers) as SectionId[]).forEach((k) => this.removeMarkerLayer(k));
  }

  /** Modo split (comparador): duas camadas clip-left/clip-right com styleFns distintas. */
  setSplitMode(enabled: boolean, leftStyleFn: ZoneStyleFn, rightStyleFn: ZoneStyleFn): void {
    if (!this.ghostLayerData || !this.map) return;
    if (enabled) this.enableSplitMode(leftStyleFn, rightStyleFn);
    else this.disableSplitMode();
  }

  /** Cria as duas camadas-metade do modo split (esquerda/direita com clip-path). */
  private enableSplitMode(leftStyleFn: ZoneStyleFn, rightStyleFn: ZoneStyleFn): void {
    const map = this.map as LeafletMap;
    if (this.bairrosLayer && map.hasLayer(this.bairrosLayer)) map.removeLayer(this.bairrosLayer);
    if (this.splitLayerLeft) map.removeLayer(this.splitLayerLeft);
    if (this.splitLayerRight) map.removeLayer(this.splitLayerRight);
    this.splitLayerLeft = this.buildSplitHalf(leftStyleFn, 'u-clip-left').addTo(map);
    this.splitLayerRight = this.buildSplitHalf(rightStyleFn, 'u-clip-right').addTo(map);
  }

  /** Monta uma metade do split: geoJSON com className de clip + hover de realce. */
  private buildSplitHalf(styleFn: ZoneStyleFn, clipClass: string): LeafletGeoJSON {
    const layerRef = { current: null as LeafletGeoJSON | null };
    const half = L.geoJSON(this.ghostLayerData as GeoJSON.GeoJsonObject, {
      style: (f) => styleFn(this.getZonaKey(f as BairroFeature)),
      className: clipClass,
      onEachFeature: (_f, l) => {
        l.on('mouseover', (e: LeafletMouseEvent) => { e.target.setStyle({ weight: 2, fillOpacity: 0.85 }); e.target.bringToFront(); });
        l.on('mouseout', (e: LeafletMouseEvent) => layerRef.current?.resetStyle(e.target));
      },
    } as GeoJSONOptions);
    layerRef.current = half;
    return half;
  }

  /** Desfaz o modo split e restaura a camada de bairros unica. */
  private disableSplitMode(): void {
    const map = this.map as LeafletMap;
    if (this.splitLayerLeft) { map.removeLayer(this.splitLayerLeft); this.splitLayerLeft = null; }
    if (this.splitLayerRight) { map.removeLayer(this.splitLayerRight); this.splitLayerRight = null; }
    if (this.bairrosLayer && !map.hasLayer(this.bairrosLayer)) this.bairrosLayer.addTo(map);
  }

  /** Posiciona e exibe o popup de zona (#u-popup) ancorado no ponto clicado. */
  showZonePopup(zonaKey: ZonaKey, latlng: LatLng): void {
    if (!this.map) return;
    const result = MANAUS_ELECTORAL_BY_YEAR[MAP_DEFAULT_YEAR][zonaKey];
    const pop = MANAUS_ZONA_POP_DATA[zonaKey];
    const popupEl = qs('#u-popup');
    if (!popupEl) return;
    byId('u-popTitle').textContent = `Zona ${MANAUS_ZONA_DISPLAY_NAME[zonaKey]}`;
    byId('u-popSub').textContent = `${pop.hab} hab · ${pop.eleitores} eleitores`;
    byId('u-popFirst').textContent = result[2];
    byId('u-popSecond').textContent = result[3];
    this.positionZonePopup(popupEl, latlng);
    popupEl.classList.add('show');
  }

  /** Calcula left/top do popup respeitando a borda direita (sidebar 460px exceto cinema). */
  private positionZonePopup(popupEl: HTMLElement, latlng: LatLng): void {
    const point = (this.map as LeafletMap).latLngToContainerPoint(latlng);
    const container = qs<HTMLElement>('.u-map-col');
    if (!container) return;
    const sidebarWidth = document.body.classList.contains('cinema-active') ? 0 : 460;
    const rightEdge = container.offsetWidth - sidebarWidth - 220;
    popupEl.style.left = `${Math.max(10, Math.min(point.x + 20, rightEdge))}px`;
    popupEl.style.top = `${Math.max(10, Math.min(point.y + 10, container.offsetHeight - 240))}px`;
  }

  flyTo(center: LatLngExpression, zoom: number, duration?: number): void {
    this.map?.flyTo(center, zoom, { duration: duration ?? 1.5 });
  }

  /** Drill-in (Explorar): voa pra zona e abre o popup; respeita prefers-reduced-motion. */
  focusZone(zonaKey: ZonaKey): void {
    const center = this.zoneCentroids[zonaKey];
    if (!this.map || !center) return;
    const reduce = prefersReducedMotion();
    this.map.flyTo(center, 13, { duration: reduce ? 0 : 1.2 });
    const show = (): void => this.showZonePopup(zonaKey, center);
    if (reduce) { show(); return; }
    this.map.once('moveend', show);
    setTimeout(show, 1500);
  }

  /** Drill-in estadual: enquadra o municipio (maxZoom 12 p/ Manaus, 9 p/ demais). */
  focusMun(munId: MunicipalityId): void {
    if (!this.map || !this.municipiosLayer) return;
    let target: L.GeoJSON | null = null;
    this.municipiosLayer.eachLayer((l) => {
      if ((l as L.GeoJSON & { feature: MunicipioFeature }).feature.properties.id === munId) target = l as L.GeoJSON;
    });
    if (!target) return;
    const reduce = prefersReducedMotion();
    this.map.flyToBounds((target as L.GeoJSON).getBounds(), { duration: reduce ? 0 : 1.2, maxZoom: munId === MANAUS_CAPITAL_ID ? 12 : 9 });
  }

  fitBairros(): void {
    if (this.bairrosLayer) this.map?.fitBounds(this.bairrosLayer.getBounds(), { padding: [30, 30] });
  }

  invalidateSize(): void {
    this.map?.invalidateSize();
  }
}

/**
 * Singleton do controlador do mapa — mesma forma de uso do `var MapController`
 * legado, agora tipado. main.ts chama MapController.init() e injeta a instancia
 * no AppContext (campo `map`) para as views.
 */
export const MapController: MapControllerContract = new LeafletMapController();
